<template>
  <div class="exchange-swap-form">
    <form
      class="exchange-swap-form__container p-16 p-20--l bor--radius-20 bg--color-01 sha--03"
      autocomplete="off"
      @submit="onSubmit"
    >
      <div class="exchange-swap-form__table-container">
        <exchange-form-side-container
          class="exchange-swap-form__form-side-container"
          without-top-space
        >
          <template #content>
            <div class="exchange-swap-form__input-header mb-4">
              <span class="exchange-swap-form__input-label txt--12px txt--height-14px txt--color-03">
                {{ $t('exchange.sellLabel') }}
              </span>
              <span
                v-if="currencySell && isLoggedIn"
                class="exchange-swap-form__label flx txt--12px txt--height-14px txt--color-03"
              >
                {{ $t('exchange.balance') }}:
                <span
                  class="exchange-swap-form__label-value txt--weight-500--l ml-4"
                >{{ currencySell.tradeAvailable | noExponents }}
                  {{ currencySell.ticker }}</span>
              </span>
            </div>

            <div class="exchange-swap-form__fields">
              <div class="input">
                <div class="vertical-line" />
                <exc-input
                  v-model="$v.valueSell.$model"
                  :field="$v.valueSell"
                  :optional-errors="optionalErrors"
                  type="number"
                  :placeholder="$t('exchange.sellLabel')"
                  step="0.00000001"
                  min="0"
                  autocomplete="off"
                  size="s"
                  without-border
                  label-disabled
                  class="exchange-swap-form__input"
                  @input="onInputSellValue"
                />
              </div>

              <exchange-ticker-dropdown-new
                v-model="currencySell"
                :options="tickerOptionsSell"
                :loading="tickerOptionsSell == null"
                :label="balanceLabel(currencySell)"
                size="s"
                balance-key="tradeAvailable"
                class="exchange-swap-form__select-currency"
                @input="onInputSellToken"
              />
            </div>
          </template>
        </exchange-form-side-container>

        <div class="exchange-swap-form__exchange-image flx flx--center">
          <img
            src="@/assets/img/exchange/exchange-image.svg"
            alt="exchange icon"
            style="cursor: pointer"
            @click="changeSides"
          >
        </div>

        <exchange-form-side-container
          class="exchange-swap-form__form-side-container exchange-swap-form__side-with-svg"
          without-top-space
        >
          <template #content>
            <div class="exchange-swap-form__input-header mb-4">
              <span class="exchange-swap-form__input-label txt--12px txt--height-14px txt--color-03">
                {{ $t('exchange.buyLabel') }}
              </span>

              <span
                v-if="currencyBuy && isLoggedIn"
                class="exchange-swap-form__label flx txt--12px txt--height-14px txt--color-03"
              >
                {{ $t('exchange.balance') }}:
                <span
                  class="exchange-swap-form__label-value txt--weight-500--l ml-4"
                >{{ currencyBuy.tradeAvailable | noExponents }}
                  {{ currencyBuy.ticker }}</span>
              </span>
            </div>

            <div class="exchange-swap-form__fields">
              <div class="input">
                <div class="vertical-line" />
                <exc-input
                  v-model="$v.valueBuy.$model"
                  :field="$v.valueBuy"
                  :optional-errors="optionalErrors"
                  type="number"
                  step="0.00000001"
                  :placeholder="$t('exchange.buyLabel')"
                  min="0"
                  autocomplete="off"
                  size="s"
                  without-border
                  label-disabled
                  class="exchange-swap-form__input"
                  @input="onInputBuyValue"
                />
              </div>
              <exchange-ticker-dropdown-new
                v-model="currencyBuy"
                :options="tickerOptionsBuy"
                :loading="tickerOptionsBuy === null"
                :label="balanceLabel(currencyBuy)"
                size="s"
                class="exchange-swap-form__select-currency"
                balance-key="tradeAvailable"
                @input="onInputBuyToken"
              />
            </div>
          </template>
        </exchange-form-side-container>
      </div>

      <div class="exchange-swap-form__submit-container mt-14 mt-32--l">
        <div class="exchange-swap-form__button-container">
          <div v-if="!isLoggedIn">
            <exc-button
              class="exchange-swap-form__submit"
              size="m"
              full-width
              :to="localePath('/login')"
            >
              {{ $t('exchange.logIn') }}
            </exc-button>
          </div>
          <exc-button
            v-else
            :disabled="$v.$invalid"
            :loading="submitting"
            full-width
            class="exchange-swap-form__submit"
            size="m"
            type="submit"
            @click="onSubmit"
          >
            {{ $t('exchange.swap') }}
          </exc-button>
        </div>
      </div>

      <exc-info-block
        v-if="currencyBuy && currencySell && !market"
        icon-position="top"
        class="exchange-swap-form__description mt-32"
      >
        <template #icon>
          <idea-icon />
        </template>
        <template #content>
          <div
            v-html="
              `Pair for <b>${currencyBuy.ticker}</b> and <b>${currencySell.ticker}</b> do not exists`
            "
          />
        </template>
      </exc-info-block>
    </form>
  </div>
</template>

<script>
import { ExcButton, ExcInput } from '@/components/common'
import { ORDER_BUY, ORDER_SELL } from '@/constants'
import { notifyError, notifySuccess } from '@/lib/bus'
import { WalletBalanceModel } from '@/lib/data-models/wallet/WalletBalanceModel.ts'
import { noExponents, parseNumber, priceFromStack } from '@/lib/utils'
import { serverFormMixin } from '@/mixins/serverFormMixin'
import Decimal from 'decimal.js-light'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'ExchangeSwapFormNew',
  components: {
    ExcInput,
    ExcButton,
    ExchangeFormSideContainer: () =>
      import(
        /* webpackMode: "eager" */ '@/components/exchange/ExchangeFormSideContainer.vue'
      ),
    ExchangeTickerDropdownNew: () =>
      import(
        /* webpackMode: "eager" */ '@/components/exchange/ExchangeTickerDropdownNew.vue'
      ),
    ExcInfoBlock: () =>
      import(
        /* webpackMode: "eager" */ '@/components/ui-kit/info-blocks/ExcInfoBlock.vue'
      ),
    IdeaIcon: () =>
      import(/* webpackMode: "eager" */ '@/assets/icons/user/bulb.svg?inline'),
  },
  filters: { noExponents },
  mixins: [validationMixin, serverFormMixin],
  data () {
    return {
      // BUY
      valueBuy: null,
      currencyBuy: null,

      // SELL
      valueSell: null,
      currencySell: null,

      submitting: false,

      ORDER_BUY,
      ORDER_SELL,

      // send to api values
      // must be equal to valueBuy or valueSell
      amount: null,

      //
      isSell: false,

      tickerOfModifiedAmount: null,

      selectedTabId: 1,
    }
  },

  computed: {
    ...mapGetters({
      balances: 'wallet/getWalletBalances',
      balanceByTicker: 'wallet/balanceByTicker',
      dBuy: 'trade/buyDepth',
      dSell: 'trade/sellDepth',
    }),
    ...mapState({
      isLoggedIn: state => state.auth.loggedIn,
      marketList: state => state.globals.marketList,
      tickerList: state => state.globals.tickerList,
      currencyImages: state => state.pairs.currencyImages,
      currencyNames: state => state.pairs.currencyNames,
    }),
    buyDepth () {
      return this.dBuy(this.selectedPair)
    },
    sellDepth () {
      return this.dSell(this.selectedPair).reverse()
    },
    allPairs () {
      return this.marketList.map(pair => pair.split('_'))
    },
    mockedBalances () {
      return this.tickerList.map(ticker =>
        WalletBalanceModel.getDefault({
          ticker,
          label: this.currencyNames[ticker],
          image: this.currencyImages[ticker],
        }),
      )
    },
    optionalErrors () {
      return {
        greaterThanZero: this.$t('validators.greaterThanZero'),
        greaterThanBalance: this.$t('validators.greaterThanBalance'),
        required: this.$t('validators.required'),
      }
    },
    tickerOptionsList () {
      return this.isLoggedIn ? this.balances : this.mockedBalances
    },
    // sell dropdown menu (from)
    tickerOptionsSell () {
      return this.tickerOptionsList // .filter(b => b.tradeBalance.greaterThan(0))
      // if (!this.currencyBuy) return this.balances

      // // find all paired tickers for selected ticker
      // return this.findTickersForSellTicker(this.currencyBuy)
    },

    // buy dropdown menu (to)
    tickerOptionsBuy () {
      if (!this.currencySell) return this.tickerOptionsList

      // find all paired tickers for selected ticker
      return this.findTickersForSellTicker(this.currencySell)
    },

    /**
     * current pair, send as market to api
     * @returns Array -> [leftTicker, rightTicker]
     */
    market () {
      if (!this.currencyBuy || !this.currencySell) return null

      const selectedPair = this.allPairs.find(([left, right]) => {
        return (
          (left === this.currencyBuy.ticker &&
            right === this.currencySell.ticker) ||
          (left === this.currencySell.ticker &&
            right === this.currencyBuy.ticker)
        )
      })

      if (!selectedPair) {
        console.error(
          `there is not pair for ${this.currencyBuy.ticker} and ${this.currencySell.ticker}`,
        )
      }

      return selectedPair
    },

    selectedPair () {
      return this.market ? this.market.join('_') : ''
    },

    /**
     * Determines direction for api body. Depends
     * on last value modified and position of ticker in pair
     */
    direction () {
      if (!this.market) return null

      const [left, right] = this.market
      if (left === this.currencySell.ticker) return ORDER_SELL
      if (right === this.currencySell.ticker) return ORDER_BUY
      // if (left === this.tickerOfModifiedAmount) return ORDER_SELL
      // if (right === this.tickerOfModifiedAmount) return ORDER_BUY
      console.error(
        'Wrong ticker of modified amount. It does not coincides with with pair (market)',
      )
      return 0
    },

    requestBody () {
      const { selectedPair, direction } = this

      // debugger
      return {
        amount: this.valueSell,
        direction: direction + '',
        market: selectedPair,
      }
    },

    stackPrice () {
      if (!this.selectedPair || !this.amount) return null

      const v = priceFromStack({
        amount: this.amount,
        ticker: this.tickerOfModifiedAmount,
        pair: this.selectedPair,
        buyOrSell:
          this.tickerOfModifiedAmount === this.currencySell.ticker
            ? 'sell'
            : 'buy',
        stack: {
          buy: this.buyDepth,
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          sell: this.sellDepth,
        },
      })

      return parseNumber(v || 0)
    },

    tabs () {
      return [
        {
          id: 1,
          text: 'Sell',
        },
        {
          id: 2,
          text: 'Buy',
        },
      ]
    },
  },

  watch: {
    selectedPair (pair) {
      this.$emit('pair-change', pair)
    },
    stackPrice (v) {
      // console.log(v)
      // if changed buy side (val or ticker) - assign sell value
      if (
        this.currencyBuy &&
        this.currencyBuy.ticker === this.tickerOfModifiedAmount
      ) {
        this.valueSell = v
      }

      // if changed sell side (val or ticker) - assign buy value
      if (
        this.currencySell &&
        this.currencySell.ticker === this.tickerOfModifiedAmount
      ) {
        this.valueBuy = v
      }
    },
    tickerOptionsSell: {
      immediate: true,
      handler (newValue) {
        if (newValue && newValue.length) {
          if (this.currencySell) {
            this.currencySell = newValue.find(
              item => item.ticker === this.currencySell?.ticker,
            )
            return null
          }

          this.currencySell =
            newValue.find(item => item.ticker === 'BTC') || newValue[1]
        }
      },
    },
    tickerOptionsBuy: {
      immediate: true,
      handler (newValue) {
        if (this.currencyBuy) {
          this.currencyBuy = newValue.find(
            item => item.ticker === this.currencyBuy?.ticker,
          )
          return null
        }

        if (newValue && newValue.length) {
          let currencyToFind
          this.currencyBuy
            ? (currencyToFind = this.currencyBuy.ticker)
            : (currencyToFind = 'USDT')
          this.currencyBuy = newValue.find(
            item => item.ticker === currencyToFind || 'BTC',
          )
        }
      },
    },
    tickerOptionsList: {
      immediate: true,
      handler (newValue) {
        if (this.currencyBuy) {
          const item = newValue.find(
            item => item.ticker === this.currencyBuy.ticker,
          )
          this.currencyBuy.image = item ? item.image : this.currencyBuy.image
        }

        if (this.currencySell) {
          const item = newValue.find(
            item => item.ticker === this.currencySell.ticker,
          )
          this.currencySell.image = item ? item.image : this.currencySell.image
        }
      },
    },
  },

  methods: {
    ...mapActions({
      updateWalletBalances: 'wallet/updateWalletBalances',
    }),
    changeSides () {
      const currencySell = this.currencySell ? { ...this.currencySell } : null
      const currencyBuy = this.currencyBuy ? { ...this.currencyBuy } : null
      this.currencySell = currencyBuy
      this.currencyBuy = currencySell

      this.onInputSellValue(this.valueSell)
    },
    balanceLabel (currency) {
      const ticker = currency && currency.ticker
      if (!ticker || !this.isLoggedIn) return '&nbsp;'
      const balance = this.balanceByTicker(ticker).tradeAvailable.toString()
      const shortBalance = parseNumber(
        this.$filters.shortifyNumber(balance, Decimal.ROUND_DOWN) || 0,
      ).toString()
      return ticker
        ? `<div style="display:flex">
                <span class="ml-auto">${this.$t(
                  'exchange.balance',
                )}:&nbsp;<span class="ml-auto txt--bold txt--main">${shortBalance} ${ticker}</span></span>
            </div>`
        : '&nbsp;'
    },
    onInputSellValue (v) {
      this.amount = v
      this.isSell = true
      this.tickerOfModifiedAmount = this.currencySell
        ? this.currencySell.ticker
        : null
    },
    onInputBuyValue (v) {
      this.amount = v
      this.isSell = false
      this.tickerOfModifiedAmount = this.currencyBuy
        ? this.currencyBuy.ticker
        : null
    },
    onInputBuyToken (v) {
      if (this.currencySell && v.ticker === this.currencySell.ticker) {
        this.amount = null
        this.valueBuy = null
        this.valueSell = null
        return
      }
      if (this.amount === this.valueBuy) {
        // debugger
        this.tickerOfModifiedAmount = v.ticker
      }
    },
    onInputSellToken (v) {
      // when changed, when buy token was chosen and there is no pair for them,
      // reset buy token
      if (!this.market) {
        this.currencyBuy = null
      }

      if (this.amount === this.valueSell) {
        // debugger
        this.tickerOfModifiedAmount = v.ticker
      }
    },
    async onSubmit () {
      this.submitting = true
      const { errors } = await this.$api.PutMarketOrder({
        body: this.requestBody,
      })
      this.submitting = false

      this.$v.$reset()

      if (errors) {
        this.mix_setServerErrors(errors)

        notifyError({
          title: this.$t('exchange.notification.title'),
          text: this.$t('exchange.notification.error'),
        })
        return
      }

      await this.updateWalletBalances()

      notifySuccess({
        title: this.$t('exchange.notification.title'),
        text: this.$t('exchange.notification.success'),
      })
    },

    findTickersForSellTicker (selectedTicker) {
      const buyTickersForSellTicker = Array.from(
        this.allPairs.reduce((acc, [left, right]) => {
          // if selected ticker is on left - get right ticker
          if (left === selectedTicker.ticker) acc.add(right)
          // and vice versa
          if (right === selectedTicker.ticker) acc.add(left)
          return acc
        }, new Set()),
      )

      return this.tickerOptionsList.filter(b =>
        buyTickersForSellTicker.includes(b.ticker),
      )
    },
    onClickTab (tab) {
      this.selectedTabId = tab.id
    },
  },

  validations () {
    return this.mix_validationsServerWrapper({
      valueBuy: {
        required,
        greaterThanZero: v => new Decimal(v || 0).greaterThan(0),
      },
      currencyBuy: {
        required,
      },
      valueSell: {
        required,
        greaterThanBalance: (v) => {
          if (!this.isLoggedIn) return true

          const ticker = this.currencySell && this.currencySell.ticker
          if (!ticker) return true
          const bal = this.balanceByTicker(ticker).tradeAvailable
          return new Decimal(v || 0).lessThanOrEqualTo(bal)
        },
        greaterThanZero: v => new Decimal(v || 0).greaterThan(0),
      },
      currencySell: {
        required,
      },
    })
  },
}
</script>

<style lang="scss" scoped>
.exchange-swap-form {
  z-index: 12;

  &__table-container {
    display: grid;
    grid-template-rows: 1fr min-content 1fr;
    gap: toRem(17px);

    @include bp(l) {
      gap: toRem(20px);
    }
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, 1fr);
    background: color('color-01');
    border-radius: toRem(22px);
    border: 1px solid color('transp-02');
    box-sizing: border-box;
    padding: toRem(14px);
    position: relative;

    @include bp(m) {
      padding: toRem(0x) toRem(22px);
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: 1fr;
    }
  }

  &__input-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: toRem(10px);
  }

  &__input-label {
    display: none;

    @include bp(m) {
      display: initial;
    }
  }

  // &__label {
  //   @include bp(l) {
  //     justify-content: end;
  //   }
  // }

  &__label-value {
    @include bp(l) {
      color: color('color-04');
    }
  }

  &__submit-container {
    border-bottom-left-radius: toRem(20px);
    border-bottom-right-radius: toRem(20px);
  }
}

.input {
  padding: 0;
  border-top: 0;
}

.vertical-line {
  display: none;
  @include bp(m) {
    display: block;
    border-left: 1px solid color('transp-02');
    height: toRem(20px);
    position: absolute;
    left: 50%;
    top: toRem(12px);
  }
}

::v-deep .exc-base-dropdown__container {
  @include bp(0, m) {
    border-top: 1px solid color('transp-03');
    padding-left: toRem(0);
    padding-right: toRem(0);
  }
}

::v-deep {
  .exc-input-label-error-wrapper__info {
    bottom: toRem(-6px);
    top: initial;

    @include bp(m) {
      top: calc(100% + 2px);
    }
  }

  .exc-base-input {
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
  }

  .exc-base-dropdown__open-icon {
    padding-right: 0 !important;
  }

  .exc-base-dropdown__options-list {
    top: toRem(16px) !important;
  }

  .exc-base-dropdown__form-control {
    padding-left: 0;

    @include bp(m) {
      padding-left: toRem(10px);
    }
  }

  .exc-input-label-error-wrapper__content {
    margin-bottom: 0;
  }
}
</style>
